import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { useGetDealShortlist } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { useGetCoInvestGpPermissions } from "@vega-api/generated/coinvest-gp-permissions/coinvest-gp-permissions";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "@vega-core/hooks/usePaginationState";
import { downloadBlobAsFile } from "@vega-core/utils/download.helpers";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { useOpportunitiesCsvUpload } from "../components/hooks/useOpportunitiesCsvUpload";
import { useProspectsCsvUpload } from "../components/hooks/useProspectsCsvUpload";
import {
  OPPORTUNITIES_UPLOAD_TEMPLATE_CONTENT,
  OPPORTUNITIES_UPLOAD_TEMPLATE_FILENAME,
  PROSPECTS_UPLOAD_TEMPLATE_CONTENT,
  PROSPECTS_UPLOAD_TEMPLATE_FILENAME,
} from "../constants/prospectsUpload";
import { SHARE_TAB_PARAM, ShareTabType } from "../constants/sharePageConstants";

import { useCoInvestDealReadOnly } from "./useCoInvestDealReadOnly";
import { useDealName } from "./useDealName";

export const useShareManagement = () => {
  const { id: dealIdFromParams } = useParams<{ id: string }>();
  const dealId = dealIdFromParams ?? "";
  const navigate = useNavigate();

  const { dealName } = useDealName(dealId);

  // #region Tab state

  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab =
    searchParams.get(SHARE_TAB_PARAM) === ShareTabType.Pending
      ? ShareTabType.Pending
      : ShareTabType.Approved;
  const setActiveTab = (tab: ShareTabType) => {
    setSearchParams({ [SHARE_TAB_PARAM]: tab });
  };

  // #endregion

  // #region Tab counts

  const { data: approvedUnfilteredResponse } = useGetDealShortlist(dealId, {
    isApproved: true,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0],
  });

  const { data: pendingUnfilteredResponse } = useGetDealShortlist(dealId, {
    isApproved: false,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0],
  });

  const approvedTotalCount = approvedUnfilteredResponse?.meta?.count;
  const pendingTotalCount = pendingUnfilteredResponse?.meta?.count;

  // #endregion

  // #region Permissions

  const { data: permissions } = useGetCoInvestGpPermissions(
    { dealId },
    {
      query: {
        enabled: !!dealId,
        select: (response) => response.data,
      },
    },
  );
  const { isMutationLocked } = useCoInvestDealReadOnly();
  // Closed deals lock all shortlist mutations except the notes column
  const canMutateShortlist = !isMutationLocked;

  const canApproveDealShortlist =
    permissions?.canFinalizeDealShortlist && canMutateShortlist;
  const canEditDealOpportunity =
    permissions?.canEditDealOpportunity && canMutateShortlist;
  // Not AND-ed with canMutateShortlist: the notes column edit stays enabled on
  // closed deals and is gated by this permission alone
  const canAddShortlistItems =
    approvedUnfilteredResponse?.data?.canAddShortlistItems ??
    pendingUnfilteredResponse?.data?.canAddShortlistItems;
  const canImportProspects = canAddShortlistItems && canMutateShortlist;

  // #endregion

  // #region Navigation

  const handleFindMoreProspects = () => {
    navigate(
      CoInvestmentFullPaths.CoInvestmentProspectsFindFromSalesforce.replace(
        ":id",
        dealId,
      ),
    );
  };

  // #endregion

  // #region CSV uploads
  // File inputs are always rendered at page level so triggers work from any tab.
  // clearSelectionTrigger is incremented on success and watched by SharePendingTable.

  const [clearSelectionTrigger, setClearSelectionTrigger] = useState(0);

  const prospectsCsvUpload = useProspectsCsvUpload({
    dealId,
    onUploadSuccess: () => setClearSelectionTrigger((prev) => prev + 1),
  });

  const opportunitiesCsvUpload = useOpportunitiesCsvUpload({
    dealId,
    onUploadSuccess: () => setClearSelectionTrigger((prev) => prev + 1),
  });

  // #endregion

  // #region CSV template downloads

  const handleDownloadProspectsCsvTemplate = () => {
    const blob = new Blob([PROSPECTS_UPLOAD_TEMPLATE_CONTENT], {
      type: "text/csv;charset=utf-8;",
    });
    downloadBlobAsFile(blob, PROSPECTS_UPLOAD_TEMPLATE_FILENAME);
  };

  const handleDownloadOpportunitiesCsvTemplate = () => {
    const blob = new Blob([OPPORTUNITIES_UPLOAD_TEMPLATE_CONTENT], {
      type: "text/csv;charset=utf-8;",
    });
    downloadBlobAsFile(blob, OPPORTUNITIES_UPLOAD_TEMPLATE_FILENAME);
  };

  // #endregion

  return {
    dealId,
    dealName,
    activeTab,
    setActiveTab,
    approvedTotalCount,
    pendingTotalCount,
    canApproveDealShortlist,
    canAddShortlistItems,
    canImportProspects,
    canEditDealOpportunity,
    handleFindMoreProspects,
    clearSelectionTrigger,
    handleProspectsUploadClick: prospectsCsvUpload.handleUploadClick,
    handleProspectsFileUpload: prospectsCsvUpload.handleFileUpload,
    handleOpportunitiesUploadClick: opportunitiesCsvUpload.handleUploadClick,
    handleOpportunitiesFileUpload: opportunitiesCsvUpload.handleFileUpload,
    handleDownloadProspectsCsvTemplate,
    handleDownloadOpportunitiesCsvTemplate,
  };
};
